<template>
    <v-card v-if="loading" flat color="white" class="d-flex flex-column fill-height justify-center pa-8" elevation="4">
        <v-progress-linear indeterminate color="primary" height="4"></v-progress-linear>
    </v-card>

    <v-card v-else-if="item" flat color="white" class="scrollbox2 fill-height pa-4" elevation="4">
        <v-toolbar dense color="#2B3848" dark>
            <v-icon v-if="item.official" color="green" class="mr-4">mdi-badge-account</v-icon>
            <v-icon v-else class="mr-4">mdi-account</v-icon>
            <v-toolbar-title>
                <span v-if="item.official">Guía oficial</span>
                <span v-else>Guía</span>
                <span class="ml-2">{{ item.name }}</span>
                <span class="ml-2 grey--text">({{ formattedGuideType }})</span>
            </v-toolbar-title>
            <v-spacer />
            <v-btn dark color="primary" @click="openGuide">Abrir ficha del guía</v-btn>
        </v-toolbar>

        <v-list>
            <v-list-item v-if="item.contactInformation.phone">
                <v-list-item-icon>
                    <v-icon>mdi-phone</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title class="text-body-2 font-weight-bold">Teléfono de contacto</v-list-item-title>
                    <v-list-item-title>{{ item.contactInformation.phone }}</v-list-item-title>
                </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="item.contactInformation.email">
                <v-list-item-icon>
                    <v-icon>mdi-email</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title class="text-body-2 font-weight-bold">Email de contacto</v-list-item-title>
                    <v-list-item-title>{{ item.contactInformation.email }}</v-list-item-title>
                </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="item.formattedLanguages">
                <v-list-item-icon>
                    <v-icon>mdi-translate</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title class="text-body-2 font-weight-bold">Idiomas que habla</v-list-item-title>
                    <v-list-item-title>
                        {{ item.formattedLanguages }}
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>
        </v-list>

        <div v-for="zone in routes" :key="zone.zoneId">
            <div v-for="(service, i) in zone.services" :key="`${zone.zoneId}-${i}`" class="d-flex flex-column pa-2 data-row text-caption">
                <div class="blue darken-2 white--text d-flex align-center pa-1 text-body-1 font-weight-bold">
                    <span>
                        <span v-if="service.status === 'PENDING'" class="grey darken-2 white--text rounded px-1">PENDIENTE</span>
                        <span v-else-if="service.status === 'PICKUP'" class="orange darken-2 white--text rounded px-1">EN RECOGIDA</span>
                        <span v-else-if="service.status === 'TOUR'" class="green white--text rounded px-1">TOUR</span>
                        <span v-else-if="service.status === 'RETURN'" class="orange darken--2 white--text rounded px-1">RETORNO</span>
                        <span v-else-if="service.status === 'CLOSED'" class="red darken--2 white--text rounded px-1">FINALIZADA</span>
                        <span v-if="service.busNumber" class="ml-4">Bus {{ service.busNumber }}</span>
                    </span>
                    <span class="ml-2 font-weight-bold">{{ service.name }}</span>
                </div>

                <div class="data-column d-flex scrollbox2-horizontal mt-2" style="width: 100%">
                    <template v-for="(meetingPoint, j) in service.meetingPoints">
                        <div :key="`mp-data-${i}-${j}`" class="d-flex flex-column align-center text-caption" :style="getMeetingPointStyle(meetingPoint)">
                            <!-- <span> -->
                            <span v-if="meetingPoint.checked" class="rounded px-1 text-body-2 font-weight-bold text-center" :class="getMeetingPointColor(meetingPoint)" style="width: 100%">
                                {{ meetingPoint.time }}
                                <span class="ml-1 font-weight-light">{{ getMeetingPointLastPickupTime(meetingPoint) }}</span>
                            </span>
                            <span v-else class="grey white--text rounded px-1 text-body-2 text-center" style="width: 100%">{{ meetingPoint.time }}</span>
                            <!-- </span> -->
                            <span class="atext-truncate text-center" style="min-width: 96px; max-width: 96px">{{ meetingPoint.name }}</span>
                        </div>

                        <v-divider inline v-if="j < service.meetingPoints.length - 1" :key="`mp-divider-${i}-${j}`" style="min-width: 16px; max-width: 32px; margin-top: 8px"></v-divider>
                    </template>
                </div>
            </div>
        </div>
    </v-card>
</template>

<style lang="scss"></style>

<script lang="ts">
import { EventBus } from "@/components/EventBus";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { BaseView } from "@/components/BaseView";
import { DataClient } from "@/services/DataClient";
import { Utilities } from "@/services/Utilities";
import { GetDayScheduleBusRoutesResponse, GetGuideResponse } from "@/services/ClientInterfaces";

@Component
export default class extends BaseView {
    @Prop({ required: true, type: String })
    itemId!: string;

    loading = true;
    item: GetGuideResponse | null = null;
    routes: GetDayScheduleBusRoutesResponse = [];

    @Watch("itemId", { immediate: true })
    async refresh() {
        this.loading = true;

        this.item = await DataClient.Guides.get({ id: this.itemId });

        const today = Utilities.todayAsYYYYMMDD();
        const routes = await DataClient.DaySchedules.getBusRoutes({ date: today });

        this.routes = [];

        for (const zone of routes) {
            const services: GetDayScheduleBusRoutesResponse[number]["services"] = [];

            for (const service of zone.services) {
                let addService = service.guideName === this.item.name;
                if (addService) {
                    services.push(service);
                }
            }

            if (services.length) {
                this.routes.push({
                    ...zone,
                    services,
                });
            }
        }

        this.loading = false;
    }

    get formattedGuideType() {
        if (this.item?.type === "SELFEMPLOYED") {
            return "Autónomo";
        } else if (this.item?.type === "BUSINESS") {
            return "Cooperativista";
        } else if (this.item?.type === "EMPLOYEE") {
            return "Empleado";
        }

        return "Desconocido";
    }

    getMeetingPointLastPickupTime(meetingPoint: GetDayScheduleBusRoutesResponse[number]["services"][number]["meetingPoints"][number]) {
        const lastPickupTime = meetingPoint.lastPickupTime;

        if (lastPickupTime) {
            // extract the time (HH:MM) from a the string like " 2023-08-04T11:48:35.433Z"
            return lastPickupTime.substring(11, 16);
        }

        return "";
    }

    getMeetingPointColor(meetingPoint: GetDayScheduleBusRoutesResponse[number]["services"][number]["meetingPoints"][number]) {
        const lastPickupTime = this.getMeetingPointLastPickupTime(meetingPoint);

        if (lastPickupTime) {
            if (lastPickupTime > meetingPoint.time) {
                return "red darken-1 white--text";
            }
        }

        return "green white--text";
    }

    getMeetingPointStyle(meetingPoint: GetDayScheduleBusRoutesResponse[number]["services"][number]["meetingPoints"][number]) {
        return "border: 3px; border-radius: 4px; padding: 4px;";
    }

    openGuide() {
        this.$emit("open", this.itemId);
    }
}
</script>
